import { ClientSmsHistory } from "@/components/clients/ClientSmsHistory";
import { ClientSmsPreferences } from "@/components/clients/ClientSmsPreferences";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate, statusLabels, statusColors } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClientsMap } from "@/components/ClientsMap";
import { NewClientDialog } from "@/components/clients/NewClientDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import ClientDuplicatesDialog from "@/components/clients/ClientDuplicatesDialog";
import BulkEditClientsDialog from "@/components/clients/BulkEditClientsDialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Users, Search, Mail, MapPin, AlertTriangle, TrendingUp, Clock, ChevronRight, Wallet, Medal, ShieldAlert, BellRing, BellOff, Send, PiggyBank, Download, Phone, Save, Pencil, Upload, FileSpreadsheet, Globe, Plus, FileText, Trash2,
  FileSignature, CheckCircle2, XCircle, Eye, MailOpen,
} from "lucide-react";
import { DisputeBadge } from "@/components/admin/DisputeBadge";
import { toast } from "sonner";
import { useTableColumns } from "@/hooks/useTableColumns";
import { ColumnsToggle, SortButton } from "@/components/TableControls";

const fmtDate = (s) => (s ? formatDate(s) : "—");

const EMAIL_TYPE_META = {
  pv_completion: { label: "PV de chantier", icon: FileSignature, color: "text-blue-700 bg-blue-50" },
  deposit_reminder: { label: "Rappel acompte", icon: AlertTriangle, color: "text-amber-700 bg-amber-50" },
  balance_reminder: { label: "Rappel solde", icon: CheckCircle2, color: "text-emerald-700 bg-emerald-50" },
};
const EMAIL_STATUS_META = {
  pending: { label: "En attente", className: "bg-slate-100 text-slate-700", Icon: Clock },
  sent: { label: "Envoyé", className: "bg-blue-100 text-blue-800", Icon: Send },
  delivered: { label: "Délivré", className: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  opened: { label: "Ouvert", className: "bg-emerald-200 text-emerald-900", Icon: MailOpen },
  bounced: { label: "Rejeté", className: "bg-red-100 text-red-800", Icon: XCircle },
  complained: { label: "Plainte", className: "bg-red-200 text-red-900", Icon: XCircle },
  delayed: { label: "Retardé", className: "bg-amber-100 text-amber-900", Icon: Clock },
  failed: { label: "Échec", className: "bg-red-100 text-red-800", Icon: XCircle },
};

const EmailTimeline = ({ emails, onResent }) => {
  const [resendingId, setResendingId] = useState(null);

  const resend = async (e) => {
    const orderRef = e.order_reference || "cet email";
    const override = window.prompt(
      `Renvoyer « ${orderRef} » à quelle adresse ? (laisser vide pour utiliser l'adresse actuelle du client)`,
      "",
    );
    if (override === null) return; // cancelled
    setResendingId(e.id);
    try {
      await api.resendClientEmail(e.id, override.trim() ? { to: override.trim() } : {});
      toast.success("Email renvoyé");
      onResent?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Renvoi échoué"));
    } finally {
      setResendingId(null);
    }
  };

  if (!emails || emails.length === 0) {
    return (
      <Card data-testid="client-email-timeline">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          <Mail className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
          Aucun email envoyé à ce client pour le moment.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="client-email-timeline">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Mail className="h-4 w-4" /> Timeline des emails
          <Badge variant="outline" className="ml-1 font-mono text-xs">{emails.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {emails.map((e) => {
          const meta = EMAIL_TYPE_META[e.type] || { label: e.type, icon: Mail, color: "text-slate-700 bg-slate-50" };
          const statusMeta = EMAIL_STATUS_META[e.status] || EMAIL_STATUS_META.sent;
          const StatusIcon = statusMeta.Icon;
          const TypeIcon = meta.icon;
          const lastEvent = e.events?.[e.events.length - 1];
          return (
            <div
              key={e.id}
              className="border rounded-md p-2.5 flex items-start gap-3 hover:bg-muted/20 transition-colors"
              data-testid={`email-row-${e.id}`}
            >
              <div className={`w-8 h-8 rounded-md ${meta.color} inline-flex items-center justify-center shrink-0`}>
                <TypeIcon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-sm">{meta.label}</span>
                  <Badge className={`${statusMeta.className} text-xs flex items-center gap-1`}>
                    <StatusIcon className="h-3 w-3" /> {statusMeta.label}
                  </Badge>
                  {e.order_reference && (
                    <span className="text-xs text-muted-foreground font-mono">{e.order_reference}</span>
                  )}
                </div>
                <div className="text-xs mt-0.5 truncate" title={e.subject}>{e.subject}</div>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
                  <span>À : {e.client_email || "—"}</span>
                  <span>•</span>
                  <span>Envoyé le {fmtDate(e.sent_at)}</span>
                  {lastEvent && lastEvent.type !== "sent" && (
                    <>
                      <span>•</span>
                      <span>{statusMeta.label.toLowerCase()} le {fmtDate(lastEvent.at)}</span>
                    </>
                  )}
                </div>
                {e.error && (
                  <div className="text-[11px] text-red-600 mt-0.5 truncate" title={e.error}>
                    ⚠ {e.error.length > 120 ? `${e.error.slice(0, 118)}…` : e.error}
                  </div>
                )}
              </div>
              {e.status === "opened" && <Eye className="h-4 w-4 text-emerald-600 shrink-0" />}
              <Button
                size="sm"
                variant="outline"
                onClick={() => resend(e)}
                disabled={resendingId === e.id || !e.order_id}
                className="shrink-0 h-7 text-xs"
                title={e.order_id ? "Renvoyer cet email" : "Commande supprimée — renvoi impossible"}
                data-testid={`email-resend-${e.id}`}
              >
                {resendingId === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                <span className="hidden sm:inline ml-1">Renvoyer</span>
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

const ClientDetailDialog = ({ open, onClose, clientKey, includeImported, onSaved, onCreateOrderForClient }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    address_line1: "", address_line2: "", postal_code: "", city: "",
    country: "", phone: "", notes: "",
    interlocutors: [],
    addresses: [],
  });
  const [saving, setSaving] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameForm, setRenameForm] = useState({ new_name: "", new_email: "" });
  const [renamePreview, setRenamePreview] = useState(null);
  const [renaming, setRenaming] = useState(false);

  useEffect(() => {
    if (!open || !clientKey) return;
    setLoading(true);
    setEditing(false);
    api.getClientDetail(clientKey, { include_imported: includeImported })
      .then(r => {
        setData(r.data);
        const h = r.data.header || {};
        setForm({
          address_line1: h.address_line1 || "",
          address_line2: h.address_line2 || "",
          postal_code: h.postal_code || "",
          city: h.profile_city || "",
          country: h.country || "",
          phone: h.phone || "",
          notes: h.notes || "",
          interlocutors: Array.isArray(h.interlocutors) ? h.interlocutors : [],
          addresses: Array.isArray(h.addresses) ? h.addresses : [],
        });
      })
      .catch(() => toast.error("Erreur chargement"))
      .finally(() => setLoading(false));
  }, [open, clientKey, includeImported]);

  const refresh = async () => {
    try {
      const { data: refreshed } = await api.getClientDetail(clientKey, { include_imported: includeImported });
      setData(refreshed);
    } catch { /* silent */ }
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      const SKIP = new Set(["interlocutors", "addresses"]);
      const clean = Object.fromEntries(
        Object.entries(form).filter(([k]) => !SKIP.has(k)).map(([k, v]) => [k, (v || "").trim() || null]),
      );
      // Interlocutors : keep as array, filter empty ones, clean strings
      clean.interlocutors = (form.interlocutors || [])
        .map((it) => ({
          name: (it.name || "").trim(),
          role: (it.role || "").trim(),
          phone: (it.phone || "").trim(),
          email: (it.email || "").trim(),
        }))
        .filter((it) => it.name || it.phone || it.email);
      // Addresses : keep as array, generate id if missing, filter empty ones
      clean.addresses = (form.addresses || [])
        .map((addr) => ({
          id: addr.id || crypto.randomUUID(),
          label: (addr.label || "").trim() || "Adresse",
          line1: (addr.line1 || "").trim(),
          line2: (addr.line2 || "").trim(),
          postal_code: (addr.postal_code || "").trim(),
          city: (addr.city || "").trim(),
          country: (addr.country || "").trim(),
          is_default: !!addr.is_default,
        }))
        .filter((addr) => addr.line1 || addr.city || addr.postal_code);
      // Ensure at most one default
      const defaults = clean.addresses.filter((a) => a.is_default);
      if (defaults.length > 1) {
        clean.addresses = clean.addresses.map((a, i) => ({ ...a, is_default: i === clean.addresses.indexOf(defaults[0]) }));
      }
      await api.updateClientProfile(clientKey, clean);
      toast.success("Fiche client enregistrée");
      setEditing(false);
      const { data: refreshed } = await api.getClientDetail(clientKey, { include_imported: includeImported });
      setData(refreshed);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const openRenameDialog = async () => {
    setRenameForm({ new_name: data?.header?.client_name || "", new_email: data?.header?.client_email || "" });
    setRenamePreview(null);
    setRenameOpen(true);
  };

  const fetchRenamePreview = async () => {
    if (!renameForm.new_name?.trim()) { setRenamePreview(null); return; }
    try {
      const { data: p } = await api.previewRenameClient(clientKey, {
        new_name: renameForm.new_name.trim(),
        new_email: renameForm.new_email?.trim() || undefined,
      });
      setRenamePreview(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aperçu indisponible");
    }
  };

  useEffect(() => {
    if (!renameOpen) return;
    const t = setTimeout(fetchRenamePreview, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameForm.new_name, renameForm.new_email, renameOpen]);

  const confirmRename = async () => {
    if (!renameForm.new_name?.trim()) {
      toast.error("Nouveau nom requis");
      return;
    }
    try {
      setRenaming(true);
      const { data: r } = await api.renameClient(clientKey, {
        new_name: renameForm.new_name.trim(),
        new_email: renameForm.new_email?.trim() || undefined,
      });
      toast.success(`Client renommé · ${r.updated_orders_count} commande(s) mise(s) à jour`);
      setRenameOpen(false);
      onSaved?.();
      // If the key changed, close current dialog so parent can reopen with the new key (or simply close)
      if (r.new_key !== clientKey) {
        onClose?.();
      } else {
        const { data: refreshed } = await api.getClientDetail(clientKey, { include_imported: includeImported });
        setData(refreshed);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du renommage");
    } finally {
      setRenaming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="client-detail-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <DialogTitle>{data?.header?.client_name || "Client"}</DialogTitle>
              <DialogDescription>Historique consolidé des commandes et paiements</DialogDescription>
            </div>
            {onCreateOrderForClient && data?.header && (
              <Button
                size="sm"
                onClick={() => onCreateOrderForClient({
                  client_name: data.header.client_name || "",
                  client_email: data.header.client_email || "",
                  phone: data.header.phone || "",
                  address_line1: data.header.address_line1 || "",
                  postal_code: data.header.postal_code || "",
                  city: data.header.profile_city || data.header.city || "",
                })}
                data-testid="client-detail-new-order-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Nouvelle commande
              </Button>
            )}
          </div>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : data ? (
          <div className="space-y-4">
            {/* Header summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card><CardContent className="pt-4">
                <div className="text-xs text-muted-foreground">Commandes</div>
                <div className="text-2xl font-bold">{data.header.orders_count}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-xs text-muted-foreground">CA HT cumulé</div>
                <div className="text-2xl font-bold text-indigo-700">{formatCurrency(data.header.total_ht)}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-xs text-muted-foreground">Encaissé</div>
                <div className="text-2xl font-bold text-green-700">{formatCurrency(data.header.total_paid)}</div>
              </CardContent></Card>
              <Card className={data.header.outstanding > 0 ? "border-red-300" : ""}><CardContent className="pt-4">
                <div className="text-xs text-muted-foreground">Restant dû</div>
                <div className={`text-2xl font-bold ${data.header.outstanding > 0 ? "text-red-700" : "text-slate-500"}`}>
                  {formatCurrency(data.header.outstanding)}
                </div>
              </CardContent></Card>
            </div>

            <Card>
              <CardContent className="pt-4 text-sm grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.header.client_email && (
                  <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {data.header.client_email}</div>
                )}
                {data.header.cities.length > 0 && (
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /> {data.header.cities.join(", ")}</div>
                )}
                {data.header.first_order_date && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Clock className="h-4 w-4" /> 1<sup>ère</sup> commande : {fmtDate(data.header.first_order_date)}</div>
                )}
                {data.header.last_order_date && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Clock className="h-4 w-4" /> Dernière : {fmtDate(data.header.last_order_date)}</div>
                )}
              </CardContent>
            </Card>

            {/* Editable profile card */}
            <Card data-testid="client-profile-card">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-700">Fiche client</h4>
                  {!editing ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openRenameDialog} data-testid="rename-client-btn">
                        Renommer
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
                        <Pencil className="h-3 w-3 mr-1" />Modifier
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(false)} disabled={saving}>Annuler</Button>
                      <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={saveProfile} disabled={saving} data-testid="save-profile-btn">
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Save className="h-3 w-3 mr-1" />Enregistrer</>}
                      </Button>
                    </div>
                  )}
                </div>
                {!editing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        {form.address_line1 || form.postal_code || form.city || form.country ? (
                          <>
                            {form.address_line1 && <div>{form.address_line1}</div>}
                            {form.address_line2 && <div>{form.address_line2}</div>}
                            <div>
                              {form.postal_code} {form.city} {form.country ? `— ${form.country}` : ""}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted-foreground italic">Adresse non renseignée</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      {form.phone ? <span>{form.phone}</span> : <span className="text-muted-foreground italic">Téléphone non renseigné</span>}
                    </div>
                    <ClientSmsPreferences key={clientKey} clientKey={clientKey} initial={data?.header?.sms_opt_out}
                      onChange={(v) => setData((d) => ({ ...d, header: { ...(d?.header || {}), sms_opt_out: v } }))} />
                    <ClientSmsHistory clientKey={clientKey} clientName={data?.header?.client_name} />
                    {form.notes && (
                      <div className="md:col-span-2 text-xs text-muted-foreground whitespace-pre-wrap border-l-2 border-slate-300 pl-2 mt-1">
                        {form.notes}
                      </div>
                    )}
                    {(form.interlocutors || []).length > 0 && (
                      <div className="md:col-span-2 mt-2 pt-2 border-t">
                        <p className="text-xs font-semibold text-muted-foreground mb-1.5">Interlocuteurs</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                          {form.interlocutors.map((it, i) => (
                            <div key={i} className="text-xs flex flex-wrap gap-x-2 items-baseline">
                              <span className="font-medium">{it.name || "—"}</span>
                              {it.role && <span className="text-muted-foreground">({it.role})</span>}
                              {it.phone && <a href={`tel:${it.phone}`} className="text-blue-600 hover:underline">{it.phone}</a>}
                              {it.email && <a href={`mailto:${it.email}`} className="text-blue-600 hover:underline">{it.email}</a>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(form.addresses || []).length > 0 && (
                      <div className="md:col-span-2 mt-2 pt-2 border-t" data-testid="addresses-readonly-list">
                        <p className="text-xs font-semibold text-muted-foreground mb-1.5">Adresses supplémentaires</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {form.addresses.map((addr, i) => (
                            <div key={addr.id || i} className="text-xs border rounded p-2 bg-zinc-50">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="font-medium">{addr.label || "Adresse"}</span>
                                {addr.is_default && <Badge variant="secondary" className="text-[9px]">Principale</Badge>}
                              </div>
                              {addr.line1 && <div>{addr.line1}</div>}
                              {addr.line2 && <div>{addr.line2}</div>}
                              <div>{addr.postal_code} {addr.city}{addr.country ? ` — ${addr.country}` : ""}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="md:col-span-2">
                      <Label className="text-xs">Adresse (ligne 1)</Label>
                      <Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} placeholder="42 rue de la République" data-testid="profile-address1" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Adresse (ligne 2)</Label>
                      <Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} placeholder="Bâtiment B, Appt 3" />
                    </div>
                    <div>
                      <Label className="text-xs">Code postal</Label>
                      <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="84430" data-testid="profile-postal" />
                    </div>
                    <div>
                      <Label className="text-xs">Ville</Label>
                      <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Mondragon" data-testid="profile-city" />
                    </div>
                    <div>
                      <Label className="text-xs">Pays</Label>
                      <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="France" />
                    </div>
                    <div>
                      <Label className="text-xs">Téléphone</Label>
                      <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="profile-phone" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Notes</Label>
                      <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Informations internes sur ce client…" data-testid="profile-notes" />
                    </div>

                    {/* Addresses (multiple labelled) */}
                    <div className="md:col-span-2 border-t pt-3 mt-1">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-semibold">Adresses supplémentaires</Label>
                        <Button
                          type="button" size="sm" variant="outline"
                          onClick={() => setForm({ ...form, addresses: [...(form.addresses || []), { id: (window.crypto?.randomUUID && window.crypto.randomUUID()) || `addr-${Date.now()}`, label: "", line1: "", line2: "", postal_code: "", city: "", country: "", is_default: false }] })}
                          data-testid="add-address-btn"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter une adresse
                        </Button>
                      </div>
                      {(form.addresses || []).length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">Aucune adresse supplémentaire. Ajoute Domicile, Bureau, Locataire, etc. pour pouvoir les sélectionner sur les commandes.</p>
                      ) : (
                        <div className="space-y-2">
                          {(form.addresses || []).map((addr, idx) => (
                            <div key={addr.id || idx} className="p-2 bg-zinc-50 rounded border space-y-2" data-testid={`address-row-${idx}`}>
                              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                                <Input
                                  placeholder="Libellé (Domicile, Bureau…)"
                                  className="md:col-span-4"
                                  value={addr.label || ""}
                                  onChange={(e) => {
                                    const arr = [...form.addresses]; arr[idx] = { ...arr[idx], label: e.target.value };
                                    setForm({ ...form, addresses: arr });
                                  }}
                                  data-testid={`address-label-${idx}`}
                                />
                                <label className="md:col-span-3 text-xs flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox" className="h-3 w-3"
                                    checked={!!addr.is_default}
                                    onChange={(e) => {
                                      const arr = (form.addresses || []).map((a, i) => ({ ...a, is_default: i === idx ? e.target.checked : false }));
                                      setForm({ ...form, addresses: arr });
                                    }}
                                    data-testid={`address-default-${idx}`}
                                  />
                                  Adresse principale
                                </label>
                                <div className="md:col-span-5 flex justify-end gap-1">
                                  <Button
                                    type="button" size="sm" variant="ghost" className="h-8 text-destructive"
                                    onClick={() => {
                                      const arr = [...form.addresses]; arr.splice(idx, 1);
                                      setForm({ ...form, addresses: arr });
                                    }}
                                    data-testid={`address-remove-${idx}`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" /> Retirer
                                  </Button>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                                <Input
                                  placeholder="Adresse (ligne 1)" className="md:col-span-6"
                                  value={addr.line1 || ""}
                                  onChange={(e) => {
                                    const arr = [...form.addresses]; arr[idx] = { ...arr[idx], line1: e.target.value };
                                    setForm({ ...form, addresses: arr });
                                  }}
                                />
                                <Input
                                  placeholder="Complément" className="md:col-span-6"
                                  value={addr.line2 || ""}
                                  onChange={(e) => {
                                    const arr = [...form.addresses]; arr[idx] = { ...arr[idx], line2: e.target.value };
                                    setForm({ ...form, addresses: arr });
                                  }}
                                />
                                <Input
                                  placeholder="CP" className="md:col-span-2"
                                  value={addr.postal_code || ""}
                                  onChange={(e) => {
                                    const arr = [...form.addresses]; arr[idx] = { ...arr[idx], postal_code: e.target.value };
                                    setForm({ ...form, addresses: arr });
                                  }}
                                />
                                <Input
                                  placeholder="Ville" className="md:col-span-6"
                                  value={addr.city || ""}
                                  onChange={(e) => {
                                    const arr = [...form.addresses]; arr[idx] = { ...arr[idx], city: e.target.value };
                                    setForm({ ...form, addresses: arr });
                                  }}
                                />
                                <Input
                                  placeholder="Pays" className="md:col-span-4"
                                  value={addr.country || ""}
                                  onChange={(e) => {
                                    const arr = [...form.addresses]; arr[idx] = { ...arr[idx], country: e.target.value };
                                    setForm({ ...form, addresses: arr });
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Interlocutors */}
                    <div className="md:col-span-2 border-t pt-3 mt-1">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-semibold">Interlocuteurs</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setForm({ ...form, interlocutors: [...(form.interlocutors || []), { name: "", role: "", phone: "", email: "" }] })}
                          data-testid="add-interlocutor-btn"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
                        </Button>
                      </div>
                      {(form.interlocutors || []).length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">Aucun interlocuteur. Ajoute le décisionnaire, le contact technique, etc.</p>
                      ) : (
                        <div className="space-y-2">
                          {(form.interlocutors || []).map((it, idx) => (
                            <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start p-2 bg-zinc-50 rounded border" data-testid={`interlocutor-row-${idx}`}>
                              <Input
                                placeholder="Nom"
                                className="md:col-span-3"
                                value={it.name || ""}
                                onChange={(e) => {
                                  const arr = [...form.interlocutors];
                                  arr[idx] = { ...arr[idx], name: e.target.value };
                                  setForm({ ...form, interlocutors: arr });
                                }}
                              />
                              <Input
                                placeholder="Rôle (ex: Décisionnaire)"
                                className="md:col-span-3"
                                value={it.role || ""}
                                onChange={(e) => {
                                  const arr = [...form.interlocutors];
                                  arr[idx] = { ...arr[idx], role: e.target.value };
                                  setForm({ ...form, interlocutors: arr });
                                }}
                              />
                              <Input
                                placeholder="Téléphone"
                                className="md:col-span-3"
                                value={it.phone || ""}
                                onChange={(e) => {
                                  const arr = [...form.interlocutors];
                                  arr[idx] = { ...arr[idx], phone: e.target.value };
                                  setForm({ ...form, interlocutors: arr });
                                }}
                              />
                              <Input
                                placeholder="Email (optionnel)"
                                className="md:col-span-2"
                                value={it.email || ""}
                                onChange={(e) => {
                                  const arr = [...form.interlocutors];
                                  arr[idx] = { ...arr[idx], email: e.target.value };
                                  setForm({ ...form, interlocutors: arr });
                                }}
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="md:col-span-1 h-9"
                                onClick={() => {
                                  const arr = [...form.interlocutors];
                                  arr.splice(idx, 1);
                                  setForm({ ...form, interlocutors: arr });
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Orders list */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Commandes du client ({data.orders?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Référence</TableHead>
                      <TableHead>Date commande</TableHead>
                      <TableHead>Ville</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">HT</TableHead>
                      <TableHead className="text-right">Payé</TableHead>
                      <TableHead className="text-right">Restant</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.orders.map(o => (
                      <TableRow
                        key={o.id}
                        className={o.outstanding > 0 && !o.deposit_paid && o.status !== "cancelled" && !o.is_on_hold ? "bg-red-50/40" : ""}
                        data-testid={`client-order-${o.id}`}
                      >
                        <TableCell className="font-mono text-xs">
                          <button
                            type="button"
                            onClick={() => {
                              onClose();
                              navigate(`/commandes?focus=${o.id}`);
                            }}
                            className="inline-flex items-center gap-1 text-primary hover:underline underline-offset-2"
                            data-testid={`client-order-link-${o.id}`}
                            title="Voir la commande"
                          >
                            {o.reference || "—"}
                            <ChevronRight className="h-3 w-3" />
                          </button>
                        </TableCell>
                        <TableCell className="text-xs">
                          {o.order_date
                            ? new Date(o.order_date).toLocaleDateString("fr-FR")
                            : <span className="text-muted-foreground">{fmtDate(o.created_at)}</span>}
                        </TableCell>
                        <TableCell className="text-xs">{o.city || "—"}</TableCell>
                        <TableCell>
                          {o.is_on_hold ? (
                            <Badge className="bg-amber-100 text-amber-900 text-xs">En pause</Badge>
                          ) : (
                            <Badge className={`${statusColors[o.status] || ""} text-xs`}>{statusLabels[o.status] || o.status}</Badge>
                          )}
                          {!o.deposit_paid && o.status !== "cancelled" && !o.is_on_hold && (
                            <Badge className="bg-red-100 text-red-800 text-xs ml-1"><AlertTriangle className="h-3 w-3 mr-1" />Acompte</Badge>
                          )}
                          {o.hidden && <Badge className="bg-slate-200 text-slate-700 text-xs ml-1">Masquée</Badge>}
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(o.amount_ht || 0)}</TableCell>
                        <TableCell className="text-right font-mono text-green-700">{formatCurrency(o.total_paid)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {o.outstanding > 0 ? <span className="text-red-700">{formatCurrency(o.outstanding)}</span> : <span className="text-muted-foreground">0</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Email timeline (Phase 84) */}
            <EmailTimeline emails={data.emails || []} onResent={refresh} />
          </div>
        ) : null}
      </DialogContent>

      {/* Rename client dialog (Phase 179) */}
      <Dialog open={renameOpen} onOpenChange={(v) => !v && setRenameOpen(false)}>
        <DialogContent className="max-w-md" data-testid="rename-client-dialog">
          <DialogHeader>
            <DialogTitle>Renommer le client</DialogTitle>
            <DialogDescription>
              Le nouveau nom sera propagé sur toutes les commandes du client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nouveau nom</Label>
              <Input
                value={renameForm.new_name}
                onChange={(e) => setRenameForm({ ...renameForm, new_name: e.target.value })}
                placeholder="Ex: SAS DUPONT BÂTIMENT"
                data-testid="rename-client-new-name"
              />
            </div>
            <div>
              <Label className="text-xs">Nouvel email (optionnel)</Label>
              <Input
                type="email"
                value={renameForm.new_email}
                onChange={(e) => setRenameForm({ ...renameForm, new_email: e.target.value })}
                placeholder="contact@exemple.com"
                data-testid="rename-client-new-email"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Si fourni, le client sera identifié par cet email (plus stable que le nom).
              </p>
            </div>
            {renamePreview && (
              <div className="rounded-md border bg-zinc-50 p-3 text-xs space-y-1" data-testid="rename-client-preview">
                <div>
                  <strong>{renamePreview.impacted_orders_count}</strong> commande(s) seront mises à jour.
                </div>
                {renamePreview.key_changes && (
                  <div className="text-amber-700">
                    L&apos;identifiant interne du client va changer (de <code className="font-mono">{renamePreview.current_key}</code> à <code className="font-mono">{renamePreview.new_key}</code>).
                  </div>
                )}
                {renamePreview.collision && (
                  <div className="text-red-700 font-semibold">
                    ⚠ Un autre client utilise déjà cette identité. Le renommage va échouer.
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)} disabled={renaming}>Annuler</Button>
            <Button
              onClick={confirmRename}
              disabled={renaming || !renameForm.new_name?.trim() || renamePreview?.collision}
              data-testid="rename-client-confirm"
            >
              {renaming ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Confirmer le renommage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export const ClientsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total_clients: 0, total_ca_ttc: 0, total_outstanding: 0, top_good_payers: [], top_at_risk: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [includeImported, setIncludeImported] = useState(false);
  const [detailKey, setDetailKey] = useState(null);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [remindingKey, setRemindingKey] = useState(null);
  const [bulkReminding, setBulkReminding] = useState(false);
  const [sortBy, setSortBy] = useState("ca_ht");
  const [importing, setImporting] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, orders_count: 0 });
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.getClients({
        ...(search && { search }),
        ...(includeImported && { include_imported: true }),
      });
      setData(data);
    } catch (_e) {
      toast.error("Erreur chargement clients");
    } finally {
      setLoading(false);
    }
  }, [search, includeImported]);

  useEffect(() => {
    const t = setTimeout(fetchData, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchData, search]);

  const handleRemind = async (e, client) => {
    e.stopPropagation();
    if (!client.client_email) {
      toast.error("Aucun email associé à ce client");
      return;
    }
    try {
      setRemindingKey(client.key);
      const { data: res } = await api.sendClientDepositReminder(client.key, {
        client_email: client.client_email,
      });
      toast.success(
        `Relance envoyée à ${res.client_email} — ${res.orders_reminded} commande${res.orders_reminded > 1 ? "s" : ""} (${res.total_due.toFixed(2)} €)`,
      );
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setRemindingKey(null);
    }
  };

  const handleRemindAll = async () => {
    const targets = (data.top_at_risk || []).filter((c) => c.client_email);
    if (targets.length === 0) return;
    setBulkReminding(true);
    let ok = 0;
    let fail = 0;
    let totalOrders = 0;
    let totalAmount = 0;
    for (const c of targets) {
      try {
        const { data: res } = await api.sendClientDepositReminder(c.key, {
          client_email: c.client_email,
        });
        ok += 1;
        totalOrders += res.orders_reminded;
        totalAmount += res.total_due;
      } catch (_e) {
        fail += 1;
      }
    }
    setBulkReminding(false);
    if (ok > 0) {
      toast.success(
        `${ok} relance${ok > 1 ? "s" : ""} envoyée${ok > 1 ? "s" : ""} (${totalOrders} cmd · ${totalAmount.toFixed(2)} €)${fail > 0 ? ` — ${fail} échec(s)` : ""}`,
      );
    } else {
      toast.error(`Aucune relance envoyée — ${fail} échec(s)`);
    }
    fetchData();
  };

  const eligibleBulkCount = (data.top_at_risk || []).filter((c) => c.client_email).length;

  // Configuration des colonnes de la table clients (tri + masquage)
  const CLIENT_COLUMNS = [
    { key: "client_name", label: "Client" },
    { key: "client_email", label: "Email" },
    { key: "cities", label: "Villes" },
    { key: "orders_count", label: "Commandes" },
    { key: "total_ht", label: "CA HT" },
    { key: "material_margin_ht", label: "Marge" },
    { key: "outstanding", label: "Encours" },
    { key: "alerts", label: "Alertes", sortable: false },
    { key: "chevron", label: "—", sortable: false, always: true },
  ];
  const {
    isVisible: isClientColVisible,
    toggleVisibility: toggleClientCol,
    handleSort: handleClientSort,
    sortArrow: clientSortArrow,
    sortConfig: clientSortConfig,
    sortItems: sortClients,
    orderedColumns: orderedClientColumns,
    moveColumn: moveClientColumn,
    resetOrder: resetClientOrder,
  } = useTableColumns({
    storageKey: "clients-table",
    columns: CLIENT_COLUMNS,
  });

  const baseSortedItems = [...(data.items || [])].sort((a, b) => {
    if (sortBy === "margin") return (b.material_margin_ht || 0) - (a.material_margin_ht || 0);
    if (sortBy === "outstanding") return (b.outstanding || 0) - (a.outstanding || 0);
    if (sortBy === "name") return (a.client_name || "").localeCompare(b.client_name || "", "fr");
    if (sortBy === "city") return ((a.profile_city || a.cities?.[0] || "") || "").localeCompare((b.profile_city || b.cities?.[0] || "") || "", "fr");
    if (sortBy === "type") return ((a.dominant_order_type_name || "") || "").localeCompare((b.dominant_order_type_name || "") || "", "fr");
    if (sortBy === "last_order") return (b.last_order_date || "").localeCompare(a.last_order_date || "");
    if (sortBy === "first_order") return (b.first_order_date || "").localeCompare(a.first_order_date || "");
    if (sortBy === "profile_created") return (b.profile_created_at || "").localeCompare(a.profile_created_at || "");
    return (b.total_ht || 0) - (a.total_ht || 0);
  });

  // Si l'utilisateur trie par colonne, on privilégie ce tri
  const sortedItems = clientSortConfig
    ? sortClients(baseSortedItems, {
        cities: (c) => (c.cities || []).join(", "),
      })
    : baseSortedItems;

  const handleBulkDelete = async () => {
    if (selectedKeys.length === 0) return;
    // Vérifie d'abord combien de commandes sont rattachées
    const impactedClients = (data.items || []).filter((c) => selectedKeys.includes(c.key));
    const ordersCount = impactedClients.reduce((s, c) => s + (c.orders_count || 0), 0);
    setDeleteConfirm({ open: true, orders_count: ordersCount });
  };

  const confirmBulkDelete = async () => {
    setDeleting(true);
    const keysSnapshot = [...selectedKeys];
    try {
      const res = await api.bulkDeleteClients({
        client_keys: keysSnapshot,
        mode: "detach",
      });
      const d = res.data;
      setSelectedKeys([]);
      setDeleteConfirm({ open: false, orders_count: 0 });
      fetchData();

      let undone = false;
      toast.success(
        `${d.profiles_deleted} client(s) supprimé(s), ${d.orders_detached} commande(s) détachée(s)`,
        {
          duration: 5000,
          action: {
            label: "Annuler",
            onClick: async () => {
              undone = true;
              try {
                await api.bulkRestoreClients({ client_keys: keysSnapshot });
                toast.success("Clients restaurés");
                fetchData();
              } catch {
                toast.error("Impossible de restaurer");
              }
            },
          },
        }
      );

      // Hard delete après timeout (sauf annulation)
      setTimeout(async () => {
        if (undone) return;
        try {
          await api.bulkDeleteClients({ client_keys: keysSnapshot, mode: "hard" });
        } catch {
          // silent : le soft delete reste
        }
      }, 5500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleSelectAll = () => {
    const visibleKeys = (sortedItems || []).map((c) => c.key);
    if (visibleKeys.every((k) => selectedKeys.includes(k))) {
      setSelectedKeys([]);
    } else {
      setSelectedKeys(visibleKeys);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const { data: blob } = await api.downloadClientsImportTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clients_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template téléchargé");
    } catch (_e) {
      toast.error("Échec du téléchargement");
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setImporting(true);
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.importClientsExcel(fd);
      toast.success(
        `${res.created_profiles} créé${res.created_profiles > 1 ? "s" : ""} · ${res.updated_profiles} mis à jour${res.skipped ? ` · ${res.skipped} ignoré${res.skipped > 1 ? "s" : ""}` : ""}`,
      );
      if (res.errors?.length) {
        toast.warning(`${res.errors.length} avertissement(s) — voir console`);
        console.warn("Import warnings:", res.errors);
      }
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'import");
    } finally {
      setImporting(false);
    }
  };

  const handleGeocodeAll = async () => {
    try {
      setGeocoding(true);
      const { data: res } = await api.geocodeAllClients();
      toast.success(
        `${res.geocoded} géolocalisé${res.geocoded > 1 ? "s" : ""}${res.failed ? ` · ${res.failed} échec(s)` : ""}`,
      );
      fetchData();
      setShowMap(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la géolocalisation");
    } finally {
      setGeocoding(false);
    }
  };

  const handleExportCsv = () => {
    if (sortedItems.length === 0) {
      toast.error("Aucun client à exporter");
      return;
    }
    const headers = [
      "Client", "Email", "Téléphone", "Adresse", "Code postal", "Ville", "Pays",
      "Villes (commandes)", "Commandes", "CA HT",
      "Type dominant",
      "Marge estimée HT", "Marge %", "Coût matière",
      "Payé", "Encours", "Encours à risque",
      "Acomptes manquants", "Commandes non soldées",
      "Jours depuis dernière relance",
      "Première commande", "Dernière commande",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const rows = sortedItems.map((c) => [
      c.client_name || "",
      c.client_email || "",
      c.phone || "",
      [c.address_line1, c.address_line2].filter(Boolean).join(" "),
      c.postal_code || "",
      c.profile_city || "",
      c.country || "",
      (c.cities || []).join(" / "),
      c.orders_count,
      (c.total_ht || 0).toFixed(2),
      c.dominant_order_type_name || "",
      c.material_margin_orders_count > 0 ? (c.material_margin_ht || 0).toFixed(2) : "",
      c.material_margin_pct !== null && c.material_margin_pct !== undefined ? c.material_margin_pct : "",
      c.material_margin_orders_count > 0 ? (c.material_cost || 0).toFixed(2) : "",
      (c.total_paid || 0).toFixed(2),
      (c.outstanding || 0).toFixed(2),
      (c.at_risk_outstanding || 0).toFixed(2),
      c.deposit_missing_count || 0,
      c.orders_unpaid_count || 0,
      c.days_since_last_reminder !== null && c.days_since_last_reminder !== undefined ? c.days_since_last_reminder : "",
      c.first_order_date ? c.first_order_date.slice(0, 10) : "",
      c.last_order_date ? c.last_order_date.slice(0, 10) : "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map(esc).join(";")).join("\n");
    // BOM so Excel opens as UTF-8
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    a.href = url;
    a.download = `clients_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${sortedItems.length} client${sortedItems.length > 1 ? "s" : ""} exporté${sortedItems.length > 1 ? "s" : ""}`);
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Users className="h-7 w-7 text-primary" />
          Clients
        </h1>
        <p className="text-muted-foreground">Vue consolidée de vos clients : CA cumulé, paiements, encours, historique</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Clients actifs</div>
          <div className="text-2xl font-bold flex items-center gap-2"><Users className="h-5 w-5 text-primary" />{data.total_clients}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">CA HT cumulé</div>
          <div className="text-2xl font-bold text-indigo-700 flex items-center gap-2"><TrendingUp className="h-5 w-5" />{formatCurrency(data.total_ca_ht ?? 0)}</div>
        </CardContent></Card>
        <Card data-testid="margin-card"><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Marge estimée cumulée</div>
          <div className={`text-2xl font-bold flex items-center gap-2 ${(data.total_material_margin_ht ?? 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            <PiggyBank className="h-5 w-5" />{formatCurrency(data.total_material_margin_ht ?? 0)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {data.total_material_cost > 0
              ? `Coût matière : ${formatCurrency(data.total_material_cost)} (${((data.total_material_margin_ht / (data.total_material_margin_ht + data.total_material_cost)) * 100 || 0).toFixed(1)}% de marge)`
              : "Aucun produit verrouillé"}
          </div>
        </CardContent></Card>
        <Card className={data.total_outstanding > 0 ? "border-red-300" : ""}><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Encours total (restant dû)</div>
          <div className={`text-2xl font-bold flex items-center gap-2 ${data.total_outstanding > 0 ? "text-red-700" : "text-slate-500"}`}>
            <Wallet className="h-5 w-5" />{formatCurrency(data.total_outstanding)}
          </div>
        </CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="clients-search"
          />
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-muted/20">
          <Switch
            id="include-imported-toggle"
            checked={includeImported}
            onCheckedChange={setIncludeImported}
            data-testid="include-imported-toggle"
          />
          <Label htmlFor="include-imported-toggle" className="text-xs cursor-pointer select-none">
            Inclure l'historique importé
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Trier par</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[160px] h-9" data-testid="clients-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ca_ht" data-testid="sort-ca-ht">CA HT décroissant</SelectItem>
              <SelectItem value="margin" data-testid="sort-margin">Marge décroissante</SelectItem>
              <SelectItem value="outstanding" data-testid="sort-outstanding">Encours décroissant</SelectItem>
              <SelectItem value="name" data-testid="sort-name">Nom (A→Z)</SelectItem>
              <SelectItem value="city" data-testid="sort-city">Ville (A→Z)</SelectItem>
              <SelectItem value="type" data-testid="sort-type">Type de commande (A→Z)</SelectItem>
              <SelectItem value="last_order" data-testid="sort-last-order">Dernière commande (récente)</SelectItem>
              <SelectItem value="first_order" data-testid="sort-first-order">Date de création (récente)</SelectItem>
              <SelectItem value="profile_created" data-testid="sort-profile-created">Fiche créée (récente)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={handleExportCsv}
          disabled={sortedItems.length === 0}
          data-testid="clients-export-csv"
        >
          <Download className="h-4 w-4 mr-2" />Exporter CSV
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={handleDownloadTemplate}
          data-testid="clients-import-template"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2" />Template Excel
        </Button>
        <Button
          size="sm"
          className="h-9"
          onClick={() => setNewClientOpen(true)}
          data-testid="clients-new-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau client
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          data-testid="clients-import-btn"
        >
          {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Importer Excel
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={handleGeocodeAll}
          disabled={geocoding || sortedItems.length === 0}
          data-testid="clients-geocode-all"
        >
          {geocoding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Globe className="h-4 w-4 mr-2" />}
          Géolocaliser tout
        </Button>
        <Button
          variant={showMap ? "default" : "outline"}
          size="sm"
          className="h-9"
          onClick={() => setShowMap(!showMap)}
          data-testid="clients-toggle-map"
        >
          <MapPin className="h-4 w-4 mr-2" />{showMap ? "Masquer carte" : "Afficher carte"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={() => setDuplicatesOpen(true)}
          data-testid="clients-duplicates-btn"
          title="Détecte les doublons clients et permet de les fusionner"
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          Doublons
        </Button>
        {selectedKeys.length > 0 && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => setBulkEditOpen(true)}
              data-testid="clients-bulk-edit-btn"
            >
              <Pencil className="h-4 w-4 mr-2" />
              Modifier ({selectedKeys.length})
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="h-9"
              onClick={handleBulkDelete}
              data-testid="clients-bulk-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer ({selectedKeys.length})
            </Button>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleImportExcel}
          className="hidden"
          data-testid="clients-import-input"
        />
      </div>

      {showMap && (
        <ClientsMap items={sortedItems} onClientClick={(key) => setDetailKey(key)} />
      )}

      {(data.top_good_payers?.length > 0 || data.top_at_risk?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {data.top_good_payers?.length > 0 && (
            <Card className="border-green-200" data-testid="top-good-payers-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Medal className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold">Top 10 bons payeurs</h3>
                  <span className="text-xs text-muted-foreground">(délai moyen de paiement)</span>
                </div>
                <div className="space-y-1.5">
                  {data.top_good_payers.map((c, i) => (
                    <div
                      key={c.key}
                      className="flex items-center justify-between text-sm py-1.5 border-b last:border-0 cursor-pointer hover:bg-green-50/50"
                      onClick={() => setDetailKey(c.key)}
                      data-testid={`good-payer-${i}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-xs font-mono text-muted-foreground w-5">#{i + 1}</span>
                        <span className="font-medium truncate">{c.client_name}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-muted-foreground whitespace-nowrap">{c.paid_orders_count} cmd payée{c.paid_orders_count > 1 ? "s" : ""}</span>
                        <Badge className="bg-green-100 text-green-800 font-mono whitespace-nowrap">
                          {c.avg_days_to_pay} j
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {data.top_at_risk?.length > 0 && (
            <Card className="border-red-200" data-testid="top-at-risk-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldAlert className="h-5 w-5 text-red-600" />
                  <h3 className="font-semibold">Top 10 clients à risque</h3>
                  <span className="text-xs text-muted-foreground">(encours le plus ancien)</span>
                  <div className="flex-1" />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs border-red-400 text-red-700 hover:bg-red-100"
                        disabled={eligibleBulkCount === 0 || bulkReminding}
                        title={
                          eligibleBulkCount === 0
                            ? "Aucun client à risque n'a d'email associé"
                            : `Relancer les ${eligibleBulkCount} client(s) avec email`
                        }
                        data-testid="at-risk-remind-all"
                      >
                        {bulkReminding ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <><Send className="h-3 w-3 mr-1" />Tout relancer ({eligibleBulkCount})</>
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="remind-all-confirm-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Relancer tous les clients à risque ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Un email de relance sera envoyé à {eligibleBulkCount} client{eligibleBulkCount > 1 ? "s" : ""} ayant un email renseigné.
                          Les clients sans email seront ignorés.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="max-h-48 overflow-y-auto text-xs space-y-1 border rounded p-2 bg-muted/30">
                        {(data.top_at_risk || []).filter((c) => c.client_email).map((c) => (
                          <div key={c.key} className="flex justify-between gap-3">
                            <span className="truncate">{c.client_name}</span>
                            <span className="text-muted-foreground font-mono whitespace-nowrap">{c.client_email}</span>
                          </div>
                        ))}
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleRemindAll}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid="remind-all-confirm-btn"
                        >
                          Envoyer les relances
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <div className="space-y-1.5">
                  {data.top_at_risk.map((c, i) => (
                    <div
                      key={c.key}
                      className="flex items-center justify-between text-sm py-1.5 border-b last:border-0 cursor-pointer hover:bg-red-50/50"
                      onClick={() => setDetailKey(c.key)}
                      data-testid={`at-risk-${i}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-xs font-mono text-muted-foreground w-5">#{i + 1}</span>
                        <span className="font-medium truncate">{c.client_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <Badge className="bg-red-100 text-red-800 font-mono whitespace-nowrap">
                          {formatCurrency(c.at_risk_outstanding ?? c.outstanding)}
                        </Badge>
                        {c.oldest_outstanding_days > 0 && (
                          <Badge variant="outline" className="text-xs whitespace-nowrap">
                            depuis {c.oldest_outstanding_days} j
                          </Badge>
                        )}
                        {c.last_reminder_at ? (
                          <Badge
                            variant="outline"
                            className={`text-xs whitespace-nowrap ${
                              c.days_since_last_reminder >= 14
                                ? "border-amber-400 text-amber-800 bg-amber-50"
                                : "border-slate-300 text-slate-700"
                            }`}
                            title={`Dernière relance le ${fmtDate(c.last_reminder_at)}`}
                          >
                            <BellRing className="h-3 w-3 mr-1" />
                            relancé il y a {c.days_since_last_reminder} j
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-xs whitespace-nowrap border-red-300 text-red-700 bg-red-50"
                            title="Aucune relance envoyée"
                          >
                            <BellOff className="h-3 w-3 mr-1" />jamais relancé
                          </Badge>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs border-red-300 text-red-700 hover:bg-red-100"
                          onClick={(e) => handleRemind(e, c)}
                          disabled={!c.client_email || remindingKey === c.key}
                          title={c.client_email ? `Envoyer une relance à ${c.client_email}` : "Pas d'email associé"}
                          data-testid={`at-risk-remind-${i}`}
                        >
                          {remindingKey === c.key ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <><Send className="h-3 w-3 mr-1" />Relancer</>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : data.items.length === 0 ? (
            <p className="text-center text-muted-foreground py-10">
              {search ? "Aucun client ne correspond à votre recherche." : "Aucun client pour le moment. Créez des commandes pour voir la liste apparaître."}
            </p>
          ) : (
            <>
              <div className="flex items-center justify-end px-4 py-2 border-b bg-slate-50/50">
                <ColumnsToggle
                  columns={CLIENT_COLUMNS}
                  orderedColumns={orderedClientColumns}
                  isVisible={isClientColVisible}
                  toggleVisibility={toggleClientCol}
                  moveColumn={moveClientColumn}
                  resetOrder={resetClientOrder}
                  testId="clients-columns-toggle"
                />
              </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={sortedItems.length > 0 && sortedItems.every((c) => selectedKeys.includes(c.key))}
                      onCheckedChange={toggleSelectAll}
                      data-testid="clients-select-all"
                      aria-label="Tout sélectionner"
                    />
                  </TableHead>
                  {orderedClientColumns.filter(c => isClientColVisible(c.key)).map(col => {
                    const rightAlign = ['orders_count','total_ht','material_margin_ht','outstanding'].includes(col.key);
                    if (col.key === 'alerts') return <TableHead key={col.key}>Alertes</TableHead>;
                    if (col.key === 'chevron') return <TableHead key={col.key}></TableHead>;
                    return (
                      <TableHead key={col.key} className={rightAlign ? "text-right" : undefined}>
                        <SortButton columnKey={col.key} label={col.label} sortArrow={clientSortArrow} handleSort={handleClientSort} />
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedItems.map((c) => {
                  const renderClientCell = (key) => {
                    switch(key) {
                      case 'client_name': return (
                        <TableCell key={key}>
                          <div className="flex items-center gap-2">
                            <div className="font-medium">{c.client_name}</div>
                            <DisputeBadge thirdPartyId={c.key} />
                          </div>
                          {c.last_order_date && (
                            <div className="text-xs text-muted-foreground">dernière commande {fmtDate(c.last_order_date)}</div>
                          )}
                        </TableCell>
                      );
                      case 'client_email': return (
                        <TableCell key={key} className="text-xs">{c.client_email || <span className="text-muted-foreground">—</span>}</TableCell>
                      );
                      case 'cities': return (
                        <TableCell key={key} className="text-xs">{c.cities.length > 0 ? c.cities.slice(0, 3).join(", ") : <span className="text-muted-foreground">—</span>}</TableCell>
                      );
                      case 'orders_count': return (
                        <TableCell key={key} className="text-right font-mono">{c.orders_count}</TableCell>
                      );
                      case 'total_ht': return (
                        <TableCell key={key} className="text-right font-mono font-semibold text-indigo-700">{formatCurrency(c.total_ht)}</TableCell>
                      );
                      case 'material_margin_ht': return (
                        <TableCell key={key} className="text-right font-mono">
                          {c.material_margin_orders_count > 0 ? (
                            <div className="flex flex-col items-end">
                              <span className={`font-semibold ${c.material_margin_ht >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                                {formatCurrency(c.material_margin_ht)}
                              </span>
                              {c.material_margin_pct !== null && c.material_margin_pct !== undefined && (
                                <span className="text-[10px] text-muted-foreground">{c.material_margin_pct}%</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                      );
                      case 'outstanding': return (
                        <TableCell key={key} className="text-right font-mono">
                          {c.outstanding > 0
                            ? <span className="text-red-700 font-semibold">{formatCurrency(c.outstanding)}</span>
                            : <span className="text-green-700">0</span>}
                        </TableCell>
                      );
                      case 'alerts': return (
                        <TableCell key={key}>
                          <div className="flex flex-wrap gap-1">
                            {c.deposit_missing_count > 0 && (
                              <Badge className="bg-red-100 text-red-800 text-xs">
                                <AlertTriangle className="h-3 w-3 mr-1" />{c.deposit_missing_count} acompte(s)
                              </Badge>
                            )}
                            {c.orders_unpaid_count > 0 && (
                              <Badge className="bg-amber-100 text-amber-900 text-xs">
                                {c.orders_unpaid_count} non soldée(s)
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      );
                      case 'chevron': return (
                        <TableCell key={key}>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </TableCell>
                      );
                      default: return null;
                    }
                  };
                  return (
                    <TableRow
                      key={c.key}
                      className={`cursor-pointer hover:bg-muted/40 ${selectedKeys.includes(c.key) ? "bg-indigo-50/60" : ""}`}
                      onClick={() => setDetailKey(c.key)}
                      data-testid={`client-row-${c.key}`}
                    >
                      <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedKeys.includes(c.key)}
                          onCheckedChange={() => toggleSelect(c.key)}
                          data-testid={`client-select-${c.key}`}
                          aria-label={`Sélectionner ${c.client_name}`}
                        />
                      </TableCell>
                      {orderedClientColumns.filter(col => isClientColVisible(col.key)).map(col => renderClientCell(col.key))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>

      <ClientDetailDialog
        open={!!detailKey}
        clientKey={detailKey}
        includeImported={includeImported}
        onClose={() => setDetailKey(null)}
        onSaved={fetchData}
        onCreateOrderForClient={(client) => {
          setDetailKey(null);
          navigate("/commandes", { state: { prefillClient: client, openCreateDialog: true } });
        }}
      />

      <NewClientDialog
        open={newClientOpen}
        onOpenChange={setNewClientOpen}
        onCreated={() => fetchData()}
        onCreateOrder={(createdRes) => {
          const p = createdRes?.profile || {};
          navigate("/commandes", {
            state: {
              prefillClient: {
                client_name: p.client_name || "",
                client_email: p.client_email || "",
                phone: p.phone || "",
                address_line1: p.address_line1 || "",
                postal_code: p.postal_code || "",
                city: p.city || "",
              },
              openCreateDialog: true,
            },
          });
        }}
      />

      <ClientDuplicatesDialog
        open={duplicatesOpen}
        onClose={() => setDuplicatesOpen(false)}
        onChanged={fetchData}
      />

      <BulkEditClientsDialog
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        selectedKeys={selectedKeys}
        onUpdated={() => {
          setSelectedKeys([]);
          fetchData();
        }}
      />

      <AlertDialog open={deleteConfirm.open} onOpenChange={(o) => !o && setDeleteConfirm({ open: false, orders_count: 0 })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selectedKeys.length} client(s) ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm.orders_count > 0 ? (
                <>
                  <strong>{deleteConfirm.orders_count} commande(s)</strong> sont rattachées à
                  {selectedKeys.length > 1 ? " ces clients." : " ce client."} Elles seront
                  <strong> détachées</strong> (conservées avec leur nom de client en texte, mais plus
                  regroupées dans l'onglet Clients). Les profils seront supprimés définitivement.
                </>
              ) : (
                "Les profils seront supprimés définitivement."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="clients-confirm-bulk-delete"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Oui, supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientsPage;
